import { useCallback, useEffect, useMemo, useState } from 'react';
import classnames from 'classnames';
import AppModal from '@/components/AppModal/AppModal';
import AppLoaderCircle from '@/components/AppLoaderCircle';
import AppIcon from '@/components/school-management/AppIcon/AppIcon';
import Button from '@/components/school-management/Button/Button';
import BulkProgress from '@/components/school-management/BulkAction/BulkProgress/BulkProgress';
import { bulkService } from '@/school-management/services/bulk';
import {
  BulkOperationResponseData,
  BulkOperationState,
} from '@/school-management/types/services/bulk';
import {
  getBulkOperationEntryId,
  getBulkOperationEntrySuccess,
} from '@/school-management/utils/bulkOperationEntry';
import type { ClassesReportTableRow } from '@/features/classes-report/normalizeGroupReportRows';
import { useAdminTranslation } from '@/admin/hooks/useAdminTranslation';
import moveStyles from '../MoveStudents/MoveStudentsPopup.module.scss';
import styles from './DeleteClassesPopup.module.scss';
import DeleteClassesDownload from './DeleteClassesDownload';

type View = 'confirm' | 'progress' | 'success' | 'partial_success' | 'failure';

export interface DeleteClassesPopupProps {
  show: boolean;
  organizationId: string;
  selectedClasses: ClassesReportTableRow[];
  onClose: () => void;
  /** Called after a completed or partial bulk delete so the list can refresh. */
  onCompleted?: () => void;
}

/** “Class & grade” column: class code + grade when both exist, else grade or code. */
export function formatClassAndGradeForDeleteSummary(row: ClassesReportTableRow): string {
  const grade = row.grade?.trim();
  const code = row.classCode?.trim();
  if (code && grade) return `${code} | ${grade}`;
  if (grade) return grade;
  if (code) return code;
  return '—';
}

const DeleteClassesPopup = ({
  show,
  organizationId,
  selectedClasses,
  onClose,
  onCompleted,
}: DeleteClassesPopupProps) => {
  const { t } = useAdminTranslation();
  const [view, setView] = useState<View>('confirm');
  const [acknowledged, setAcknowledged] = useState(false);
  const [operationId, setOperationId] = useState('');
  const [bulkOperation, setBulkOperation] = useState<BulkOperationResponseData>();
  const [awaitingOperationId, setAwaitingOperationId] = useState(false);

  useEffect(() => {
    if (!show) return;
    setView('confirm');
    setAcknowledged(false);
    setOperationId('');
    setBulkOperation(undefined);
    setAwaitingOperationId(false);
  }, [show]);

  const hasAnyStudents = useMemo(
    () => selectedClasses.some(c => (c.students || 0) > 0),
    [selectedClasses]
  );

  const totalStudents = useMemo(
    () => selectedClasses.reduce((sum, c) => sum + (c.students || 0), 0),
    [selectedClasses]
  );

  const startBulkDelete = useCallback(async () => {
    if (!organizationId || selectedClasses.length === 0) {
      setView('failure');
      return;
    }

    setView('progress');
    setAwaitingOperationId(true);
    setOperationId('');
    setBulkOperation(undefined);

    try {
      const result = await bulkService.bulkDeleteGroups(organizationId, {
        groupIds: selectedClasses.map(c => c.id),
        reason: t('bulkActions.deleteClasses.apiReason'),
      });
      const opId = result.data?.data?.operationId?.trim() || '';
      if (!opId) {
        setView('failure');
        return;
      }
      setOperationId(opId);
    } catch {
      setView('failure');
    } finally {
      setAwaitingOperationId(false);
    }
  }, [organizationId, selectedClasses, t]);

  const handleBulkFinish = useCallback(
    (op: BulkOperationResponseData) => {
      setBulkOperation(op);
      switch (op.state) {
        case BulkOperationState.Completed:
          onCompleted?.();
          setView('success');
          break;
        case BulkOperationState.PartialSuccess:
          onCompleted?.();
          setView('partial_success');
          break;
        case BulkOperationState.Failed:
          setView('failure');
          break;
        default:
          break;
      }
    },
    [onCompleted]
  );

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const partialRows = useMemo(() => {
    const results = bulkOperation?.results || [];
    const map = results.reduce<Record<string, boolean>>((acc, r) => {
      const id = getBulkOperationEntryId(r);
      if (id) acc[id] = getBulkOperationEntrySuccess(r);
      return acc;
    }, {});
    return selectedClasses.map(row => ({
      row,
      success: map[row.id] ?? false,
    }));
  }, [bulkOperation, selectedClasses]);

  const deletedStudentsFromSuccessfulDeletes = useMemo(() => {
    if (!bulkOperation) return 0;
    if (bulkOperation.state === BulkOperationState.Completed) {
      return selectedClasses.reduce((sum, row) => sum + (row.students || 0), 0);
    }
    if (bulkOperation.state === BulkOperationState.PartialSuccess) {
      return partialRows.reduce(
        (sum, { row, success }) => (success ? sum + (row.students || 0) : sum),
        0
      );
    }
    return 0;
  }, [bulkOperation, selectedClasses, partialRows]);

  const studentsDeletedFollowUpBlock =
    deletedStudentsFromSuccessfulDeletes > 0 ? (
      <div className={classnames(moveStyles.description, styles.studentsDeletedFollowUp)}>
        {deletedStudentsFromSuccessfulDeletes === 1
          ? t('bulkActions.deleteClasses.successes.studentsDeletedFollowUpSingular')
          : t('bulkActions.deleteClasses.successes.studentsDeletedFollowUpPlural', {
              count: deletedStudentsFromSuccessfulDeletes,
            })}
      </div>
    ) : null;

  const renderContent = () => {
    switch (view) {
      case 'confirm':
        return (
          <>
            <div className={moveStyles.title}>
              {t('bulkActions.deleteClasses.confirm.titleWithStudents')}
            </div>
            <p
              className={hasAnyStudents ? moveStyles.description : styles.confirmWarningMuted}
              role={hasAnyStudents ? 'alert' : undefined}
            >
              {hasAnyStudents
                ? t('bulkActions.deleteClasses.confirm.warningWithStudents', {
                    classCount: selectedClasses.length,
                    studentCount: totalStudents,
                  })
                : t('bulkActions.deleteClasses.confirm.warningEmpty')}
            </p>
            <div className={styles.summaryTableWrap}>
              <table className={styles.summaryTable}>
                <thead>
                  <tr>
                    <th>{t('bulkActions.common.name')}</th>
                    <th>{t('bulkActions.common.teacher')}</th>
                    <th>{t('bulkActions.common.classAndGrade')}</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedClasses.map(c => (
                    <tr key={c.id}>
                      <td>{c.name}</td>
                      <td>{c.teacher}</td>
                      <td>{formatClassAndGradeForDeleteSummary(c)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {hasAnyStudents ? (
              <div className={styles.checkboxRow}>
                <input
                  id='delete-classes-understand'
                  type='checkbox'
                  className={styles.consentCheckbox}
                  checked={acknowledged}
                  onChange={e => setAcknowledged(e.target.checked)}
                />
                <label htmlFor='delete-classes-understand'>
                  {t('bulkActions.deleteClasses.confirm.understandWithStudents')}
                </label>
              </div>
            ) : null}
            <div className={styles.confirmActions}>
              <button type='button' className={styles.backLink} onClick={handleClose}>
                {t('common.back')}
              </button>
              <Button
                className={classnames(moveStyles.button, styles.deleteAnywayButton)}
                disabled={hasAnyStudents && !acknowledged}
                onClick={() => void startBulkDelete()}
              >
                {t('bulkActions.deleteClasses.confirm.deleteAnywayButton')}
              </Button>
            </div>
          </>
        );
      case 'progress':
        return (
          <>
            {awaitingOperationId || !operationId ? (
              <AppLoaderCircle />
            ) : (
              <BulkProgress operationId={operationId} onFinish={handleBulkFinish} />
            )}
          </>
        );
      case 'success':
        return (
          <>
            <div className={moveStyles.title}>{t('bulkActions.deleteClasses.successes.title')}</div>
            {studentsDeletedFollowUpBlock}
            <DeleteClassesDownload
              selectedClasses={selectedClasses}
              bulkOperation={bulkOperation}
              bulkOperationState={BulkOperationState.Completed}
            />
            <div className={moveStyles.footer}>
              <Button className={moveStyles.button} onClick={handleClose}>
                {t('done')}
              </Button>
            </div>
          </>
        );
      case 'partial_success':
        return (
          <>
            <div className={moveStyles.title} style={{ fontSize: '26px' }}>
              {t('bulkActions.common.partialSuccessTitle')}
            </div>
            <div className={moveStyles.description}>
              {t('bulkActions.deleteClasses.partialSuccesses.description')}
            </div>
            <div className={classnames(styles.summaryTableWrap, styles.partialSuccessTableWrap)}>
              <table className={classnames(styles.summaryTable, styles.partialSuccessTable)}>
                <thead>
                  <tr>
                    <th>{t('bulkActions.common.classAndGrade')}</th>
                    <th>{t('bulkActions.common.teacher')}</th>
                    <th>{t('bulkActions.deleteClasses.partialSuccesses.statusColumn')}</th>
                    <th>{t('bulkActions.deleteClasses.partialSuccesses.tableStudentsDeleted')}</th>
                  </tr>
                </thead>
                <tbody>
                  {partialRows.map(({ row, success }) => {
                    const totalStudents = row.students || 0;
                    const deletedStudents = success ? totalStudents : 0;
                    const statusKey = success ? 'statusDeleted' : 'statusFailed';
                    const statusIcon = success ? '✅' : '❌';

                    return (
                      <tr key={row.id}>
                        <td className={styles.partialSuccessClassCell}>
                          {formatClassAndGradeForDeleteSummary(row)}
                        </td>
                        <td>{row.teacher}</td>
                        <td className={styles.partialSuccessStatusCell}>
                          <span aria-hidden>{statusIcon}</span>
                          <span>
                            {t(`bulkActions.deleteClasses.partialSuccesses.${statusKey}`)}
                          </span>
                        </td>
                        <td>
                          {t('bulkActions.deleteClasses.partialSuccesses.studentsDeletedRatio', {
                            deleted: deletedStudents,
                            total: totalStudents,
                          })}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <DeleteClassesDownload
              selectedClasses={selectedClasses}
              bulkOperation={bulkOperation}
              bulkOperationState={BulkOperationState.PartialSuccess}
            />
            <div className={moveStyles.footer}>
              <Button className={moveStyles.button} onClick={handleClose}>
                {t('ok')}
              </Button>
            </div>
          </>
        );
      case 'failure':
        return (
          <>
            <AppIcon size={80} icon='error' className={moveStyles.icon} />
            <div className={moveStyles.title} style={{ fontSize: '26px' }}>
              {t('error.weHadAProblem')}
            </div>
            <div className={moveStyles.description}>{t('common.tryAgainLater')}</div>
            <DeleteClassesDownload
              selectedClasses={selectedClasses}
              bulkOperation={bulkOperation}
              bulkOperationState={BulkOperationState.Failed}
            />
            <div className={moveStyles.footer} style={{ width: '433px', flexDirection: 'column' }}>
              <Button className={moveStyles.button} onClick={() => void startBulkDelete()}>
                {t('common.tryAgain')}
              </Button>
              <Button className={moveStyles.textButton} onClick={handleClose}>
                {t('common.cancel')}
              </Button>
            </div>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <AppModal
      show={show}
      className={moveStyles.modal}
      modalContentClassName={classnames(moveStyles.modalContent, {
        [moveStyles.progressModalContent]: view === 'progress',
        [styles.modalContentWide]: view === 'confirm' || view === 'partial_success',
        [styles.modalContentResult]: view === 'success' || view === 'failure',
      })}
      type='fade'
      blockBodyScroll={true}
    >
      {renderContent()}
    </AppModal>
  );
};

export default DeleteClassesPopup;
